<template>
  <ExcursionFilterLine ref="filterLine">
    <template #filters>
      <ExcursionFilterDatesSkeleton>
        <ExcursionFilterDates
          v-if="date"
          show-close-button
          is-fullscreen
          :slug
          v-bind="createFilterHandlers('date')"
        />
      </ExcursionFilterDatesSkeleton>

      <ExcursionFilterFormatSkeleton v-if="!isMobile">
        <ExcursionFiltersFormat
          v-if="!isSkeletonActive"
          :is-fullscreen="isTablet"
          :is-scroll="isScroll"
          class="filter-item"
          v-bind="createFilterHandlers('type')"
        />
      </ExcursionFilterFormatSkeleton>

      <ExcursionFilterMovementSkeleton v-if="!isMobile">
        <ExcursionMovementTypePopup
          v-if="!isSkeletonActive"
          :aggregation-movement-type="movementType"
          :is-fullscreen="isTablet"
          :is-scroll="isScroll"
          class="filter-item"
          v-bind="createFilterHandlers('movement_type')"
          @update:selectedTypes="selectedTypes = $event"
        />
      </ExcursionFilterMovementSkeleton>
      <ExcursionFilterPriceSkeleton v-if="!isMobile">
        <ExcursionFilterPrice
          v-if="defaultPrice"
          :currency-code="props.currencyCode"
          :histogram="priceData.histogram"
          :min-price="priceData.minPrice"
          :max-price="priceData.maxPrice"
          :is-fullscreen="isTablet"
          :is-scroll="isScroll"
          class="filter-item"
          v-bind="createFilterHandlers('price')"
        />
      </ExcursionFilterPriceSkeleton>
    </template>

    <template #modal-button>
      <ExcursionModalFilter
        :currency-code="props.currencyCode"
        :aggregation-movement-type="movementType"
        :is-scroll="isScroll"
        :price="priceData"
        v-bind="createFilterHandlers('all')"
      />
    </template>
  </ExcursionFilterLine>
</template>

<script setup lang="ts">
  import { useExcursionFiltersStore, type TExcursionFiltersState } from '@/entities/filter/store/excursion';
  import { ExcursionFilterMovementSkeleton, ExcursionMovementTypePopup } from '@/features/filter/movement-type';
  import { ExcursionFilterDates, ExcursionFilterDatesSkeleton } from '@/features/filter/dates';
  import { ExcursionFilterFormatSkeleton, ExcursionFiltersFormat } from '@/features/filter/format';
  import { ExcursionFilterPrice, ExcursionFilterPriceSkeleton } from '@/features/filter/price';
  import {
    useExcursionExperienceAggregations,
    type ExcursionParams,
  } from '@/entities/experience/aggregation/api/excursion/useExcursionExperienceAggregations.ts';
  import ExcursionModalFilter from './ExcursionModalFilter.vue';
  import { useScreenSize } from '@experience/shared';
  import { excursionFiltersToQuery } from '@/entities/filter/store/excursion';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import {
    type ExcursionSoguFilterTypes,
    useExcursionFilterBlockSogu,
  } from '@/widgets/filtersBlock/composables/excursion/useExcursionFilterBlockSogu.ts';
  import { ref, computed, watch } from 'vue';
  import type { CurrencyCode } from '@/shared/lib/currency';
  import { ExcursionFilterLine } from '@/entities/filter/filter-line';
  import { until } from '@vueuse/core';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { useExcursionListingSkeleton } from '@/shared/composables/excursion/useExcursionDelayedListingSkeleton.ts';

  // Интерфейс для экспортируемых свойств FilterLine
  interface FilterLineExpose {
    isScroll: boolean;
    isOverlowFilterScroll: boolean;
    isShowGradient: boolean;
  }

  const filtersStore = useExcursionFiltersStore();
  const seoStore = useSeoStore();
  const { isSkeletonActive } = useExcursionListingSkeleton();

  const { isMobile, isTablet } = useScreenSize();
  const filterLine = ref<FilterLineExpose | null>(null);

  interface Props {
    page: number;
    slug: string;
    defaultSoguFields: ContextGeoSubject;
    currencyCode: CurrencyCode;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    updated: [];
  }>();

  const isScroll = computed(() => filterLine.value?.isScroll || false);

  const aggregationParams = computed((): ExcursionParams => {
    const pageParams = {
      city: props.slug,
    };

    const queryParams = excursionFiltersToQuery(filtersStore.searchState, pageParams);

    return {
      city__slug: queryParams.city__slug,
      type: queryParams.type,
      start_price: queryParams.start_price,
      end_price: queryParams.end_price,
      start_duration: queryParams.start_duration,
      end_duration: queryParams.end_duration,
      start_date: queryParams.start_date,
      end_date: queryParams.end_date,
      movement_type: queryParams.movement_type,
      persons_count: queryParams.persons_count,
      tag__slugs: queryParams.tag__slugs,
      sorting: queryParams.sorting,
      children_allowed: queryParams.children_allowed,
      start_time_interval: queryParams.start_time_interval,
      duration_interval: queryParams.duration_interval,
      page: props.page,
    };
  });

  const {
    date,
    movementType,
    price: defaultPrice,
    isExperienceAggregationsPending,
  } = useExcursionExperienceAggregations(
    computed(() => {
      return {
        city__slug: aggregationParams.value.city__slug,
        page: aggregationParams.value.page,
        persons_count: aggregationParams.value.persons_count,
        type:
          filtersStore.searchState.selected_rubric?.is_tours_included === true
            ? 'private,group,ticket,transfer_service,tour'
            : 'private,group,ticket,transfer_service',
      };
    }),
  );

  const selectedTypes = ref<string[]>([]);

  const priceData = computed(() => ({
    histogram: defaultPrice.value?.defaultHistogram as Record<string, number>,
    minPrice: defaultPrice.value?.defaultRange?.[0] as number,
    maxPrice: defaultPrice.value?.defaultRange?.[1] as number,
  }));

  const { excursionSoguFilterApplyEvent, excursionSoguFilterOpenEvent, excursionSoguFilterResetEvent } =
    useExcursionFilterBlockSogu();

  const filtersScroll = async () => {
    await until(isExperienceAggregationsPending).toBe(false);
    emit('updated');
  };

  const createFilterHandlers = (type: ExcursionSoguFilterTypes) => ({
    onOpen: (isForced: boolean) => excursionSoguFilterOpenEvent(props.defaultSoguFields, type, isForced),
    onApply: (isForced: boolean, filterData?: Partial<TExcursionFiltersState>) => {
      excursionSoguFilterApplyEvent(props.defaultSoguFields, type, isForced, filterData);
      filtersScroll();
    },
    onReset: (isForced: boolean) => excursionSoguFilterResetEvent(props.defaultSoguFields, type, isForced),
  });

  watch(
    () => [priceData.value.minPrice, priceData.value.maxPrice],
    ([minPrice, maxPrice]) => {
      seoStore.setPriceInfo(minPrice, maxPrice, props.currencyCode);
    },
    {
      deep: true,
      immediate: true,
    },
  );
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filter-item {
    @include adaptivity.mobile {
      display: none;
    }
  }
</style>
